import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const adminHttp = axios.create({ baseURL: API, withCredentials: true });

const AdminAuthContext = createContext(null);
export const useAdminAuth = () => useContext(AdminAuthContext);

const INACTIVITY_MS = 30 * 60 * 1000;   // 30 minutes
const PING_INTERVAL_MS = 5 * 60 * 1000; // renew session every 5 min via /me

const formatDetail = (d) => {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d.msg || String(d);
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingOtpEmail, setPendingOtpEmail] = useState(null);
  const [devOtp, setDevOtp] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const lastActivity = useRef(Date.now());

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await adminHttp.get("/admin/auth/me");
      setAdmin(data);
    } catch {
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await adminHttp.post("/admin/auth/login", { email, password });
      setPendingOtpEmail(data.email);
      if (data.dev_otp) setDevOtp(data.dev_otp);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatDetail(e.response?.data?.detail) };
    }
  };

  const verifyOtp = async (email, otp) => {
    try {
      const { data } = await adminHttp.post("/admin/auth/verify-otp", { email, otp });
      setAdmin(data);
      setPendingOtpEmail(null); setDevOtp(null);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatDetail(e.response?.data?.detail) };
    }
  };

  const logout = useCallback(async () => {
    try { await adminHttp.post("/admin/auth/logout"); } catch (e) { console.warn("[admin-auth] logout request failed", e); }
    setAdmin(null);
    setPendingOtpEmail(null); setDevOtp(null);
    if (location.pathname.startsWith("/admin")) navigate("/admin", { replace: true });
  }, [navigate, location.pathname]);

  // Inactivity auto-logout + periodic session renewal
  useEffect(() => {
    if (!admin) return;
    const markActive = () => { lastActivity.current = Date.now(); };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, markActive, { passive: true }));

    const idleCheck = setInterval(() => {
      if (Date.now() - lastActivity.current > INACTIVITY_MS) {
        logout();
      }
    }, 60 * 1000);
    const renew = setInterval(() => { adminHttp.get("/admin/auth/me").catch(() => {}); }, PING_INTERVAL_MS);

    return () => {
      events.forEach((e) => window.removeEventListener(e, markActive));
      clearInterval(idleCheck);
      clearInterval(renew);
    };
  }, [admin, logout]);

  return (
    <AdminAuthContext.Provider value={{ admin, loading, login, verifyOtp, logout, pendingOtpEmail, devOtp, api: adminHttp }}>
      {children}
    </AdminAuthContext.Provider>
  );
};
